import React, { useCallback, useEffect, useState } from 'react';
import { fetchAuditoriaEscolhas } from '../../../api/portalCotacoes/portalCotacoesApi';
import {
  AuditoriaEscolhasAtuais as Dados,
  MOTIVO_SEM_COMPRA_LABEL,
} from '../../../interfaces/PortalCotacoes';
import { notify } from '../../../shared/feedback/notify';
import './AuditoriaPropostaModal.css';

/**
 * QUADRO ATUAL das escolhas (14/08/2026, pedido do Victor).
 *
 * A linha do tempo responde "o que mudou, quando e por quem". Com muitos salvamentos, ela
 * deixa de responder a pergunta seguinte — *"ok, entendi que isso foi alterado, mas como
 * está a cotação agora?"*. Esta aba é essa foto: uma linha por item, com a decisão que vale
 * neste momento e o que ela custa contra o menor preço.
 *
 * ⚠️ É o AGORA, não a auditoria de um instante — e a tela diz isso, para ninguém ler este
 * quadro como se fosse o estado de alguma data. O histórico continua na linha do tempo.
 */

interface AuditoriaEscolhasAtuaisProps {
  cotacaoId: string;
}

function moeda(valor: number | null): string {
  if (valor === null) return '—';
  return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function prazo(dias: number | null): string {
  if (dias === null) return '—';
  return dias === 0 ? 'Pronta entrega' : `${dias} ${dias === 1 ? 'dia' : 'dias'}`;
}

const APROVACAO_LABEL: Record<string, string> = {
  PENDENTE: 'Aguardando gestor',
  APROVADA: 'Aprovado',
  REPROVADA: 'Reprovado',
};

/**
 * Situação de aprovação de UMA escolha (14/08/2026, corrigindo o bug que o Victor
 * reproduziu: o item aparecia "Reprovado" depois de o comprador redecidir no menor preço).
 *
 * Quatro casos, e nenhum pode se confundir com os outros:
 *  - **menor preço**: não passa pelo gestor — aprovado direto pela regra de governança;
 *  - **divergente já decidida pelo gestor**: aprovado / reprovado / aguardando;
 *  - **divergente ainda não enviada**: a aprovação nasce no fechamento da cesta;
 *  - **sem escolha**: não há o que aprovar.
 */
function situacaoDaAprovacao(item: {
  fornecedor: string | null;
  statusAprovacao: string | null;
  requerAprovacao: boolean;
}): { texto: string; detalhe?: string } {
  if (!item.fornecedor) return { texto: '—' };
  if (item.statusAprovacao) return { texto: APROVACAO_LABEL[item.statusAprovacao] ?? item.statusAprovacao };
  if (!item.requerAprovacao) return { texto: 'Aprovado direto', detalhe: 'menor preço' };
  return { texto: 'Ao fechar a cesta' };
}

const AuditoriaEscolhasAtuais: React.FC<AuditoriaEscolhasAtuaisProps> = ({ cotacaoId }) => {
  const [dados, setDados] = useState<Dados | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const resposta = await fetchAuditoriaEscolhas(cotacaoId);
      setDados(resposta);
      setErro(null);
    } catch (error) {
      // Quadro vazio leria como "nada foi escolhido" — afirmação sobre a cotação tirada de
      // uma falha de rede, e é justamente o quadro geral que se veio consultar.
      setDados(null);
      setErro('Não foi possível carregar as escolhas atuais. Tente novamente; se persistir, acione a TI.');
      notify.errorFrom(error, 'Não foi possível carregar as escolhas atuais.');
    } finally {
      setCarregando(false);
    }
  }, [cotacaoId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  if (carregando) return <div className="auditoria-empty-state">Carregando as escolhas atuais...</div>;

  if (erro) {
    return (
      <div className="auditoria-error-state" role="alert">
        {erro}
      </div>
    );
  }

  if (!dados) return null;

  const { itens, totais } = dados;

  return (
    <>
      <p className="auditoria-escolhas-nota">
        Este é o estado <strong>atual</strong> da cotação, depois de todas as alterações. O histórico de cada
        mudança está na <strong>Linha do Tempo</strong>.
      </p>

      {/* Números que respondem o "quadro geral" de relance, antes da tabela. */}
      <div className="auditoria-escolhas-totais">
        <div>
          <span className="auditoria-escolhas-rotulo">Total escolhido</span>
          <strong>{moeda(totais.escolhido)}</strong>
        </div>
        <div>
          <span className="auditoria-escolhas-rotulo">Soma dos menores preços</span>
          <strong>{moeda(totais.menorPreco)}</strong>
        </div>
        <div>
          <span className="auditoria-escolhas-rotulo">Diferença</span>
          <strong className={totais.diferenca > 0 ? 'auditoria-escolhas-acima' : undefined}>
            {totais.diferenca > 0 ? `+${moeda(totais.diferenca)}` : moeda(totais.diferenca)}
          </strong>
        </div>
      </div>

      {(totais.semDecisao > 0 || totais.acimaDoMenorPreco > 0) && (
        <p className="auditoria-proposta-aviso">
          {totais.semDecisao > 0 && (
            <>
              {totais.semDecisao === 1
                ? '1 item ainda sem decisão'
                : `${totais.semDecisao} itens ainda sem decisão`}
              {totais.acimaDoMenorPreco > 0 ? '. ' : '.'}
            </>
          )}
          {totais.acimaDoMenorPreco > 0 && (
            <>
              {totais.acimaDoMenorPreco === 1
                ? '1 escolha acima do menor preço'
                : `${totais.acimaDoMenorPreco} escolhas acima do menor preço`}{' '}
              — passam pela aprovação do gestor.
            </>
          )}
        </p>
      )}

      <div className="auditoria-proposta-tabela-wrapper">
        <table className="auditoria-proposta-tabela">
          <thead>
            <tr>
              <th>Peça</th>
              <th>Fornecedor escolhido</th>
              <th>Valor</th>
              <th>Prazo</th>
              <th>Menor preço</th>
              <th>Aprovação</th>
            </tr>
          </thead>
          <tbody>
            {itens.map((item) => (
              <tr key={item.cotacaoItemId}>
                <td>
                  <div>{item.descricao}</div>
                  <div className="auditoria-proposta-secundario">
                    {item.codigoProduto} · {item.quantidade} {item.unidade}
                  </div>
                  {item.justificativa && (
                    <div className="auditoria-proposta-secundario">Justificativa: {item.justificativa}</div>
                  )}
                  {item.comentarioGestor && (
                    <div className="auditoria-proposta-secundario">Gestor: {item.comentarioGestor}</div>
                  )}
                </td>

                {/*
                  Três estados distintos, e nenhum pode se confundir com os outros:
                  ESCOLHIDO (tem fornecedor), SEM COMPRA (decisão tomada, com motivo) e
                  SEM DECISÃO (trabalho que falta). Colapsá-los faria o quadro geral mentir
                  sobre o que ainda precisa ser feito.
                */}
                {item.fornecedor ? (
                  <>
                    <td>{item.fornecedor}</td>
                    <td>{moeda(item.valor)}</td>
                    <td>{prazo(item.prazoEntregaDias)}</td>
                    <td>
                      {item.foiMenorPreco ? (
                        'Sim'
                      ) : (
                        <>
                          Não
                          {item.menorPrecoDisponivel && (
                            <div className="auditoria-proposta-secundario">
                              menor: {moeda(item.menorPrecoDisponivel.totalItem)}
                            </div>
                          )}
                        </>
                      )}
                    </td>
                    <td>
                      {(() => {
                        const situacao = situacaoDaAprovacao(item);
                        return (
                          <>
                            {situacao.texto}
                            {situacao.detalhe && (
                              <div className="auditoria-proposta-secundario">{situacao.detalhe}</div>
                            )}
                          </>
                        );
                      })()}
                    </td>
                  </>
                ) : item.decidido ? (
                  <td colSpan={5} className="auditoria-proposta-nao-cotou">
                    Sem compra
                    {item.motivoSemCompra
                      ? ` — ${MOTIVO_SEM_COMPRA_LABEL[item.motivoSemCompra] ?? item.motivoSemCompra}`
                      : ''}
                  </td>
                ) : (
                  <td colSpan={5} className="auditoria-escolhas-pendente">
                    Ainda sem decisão
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default AuditoriaEscolhasAtuais;
